import { ReactNode } from 'react';
import { useSidebar } from '@/hooks/use-sidebar';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

interface DynamicMainProps {
  children: ReactNode;
  className?: string;
}

/**
 * DynamicMain - Mobile-first responsive main content wrapper
 *
 * Handles responsive layout for mobile, tablet, and desktop with proper sidebar spacing
 */
export function DynamicMain({ children, className }: DynamicMainProps) {
  const { collapsed } = useSidebar();
  const isMobile = useIsMobile();

  return (
    <main
      className={cn(
        // Mobile-first: no padding initially, add as screen grows
        'min-h-screen w-full',
        // Mobile: full width with minimal padding
        'p-3 pt-16', // pt-16 for mobile header space
        // Tablet: slightly more padding
        'sm:p-4 sm:pt-18',
        // Desktop: sidebar spacing
        'md:p-6',
        // Large screens: dynamic sidebar spacing
        {
          'lg:pl-[90px]': !isMobile && collapsed,
          'lg:pl-[260px]': !isMobile && !collapsed,
          'xl:pl-[90px]': !isMobile && collapsed,
          'xl:pl-[280px]': !isMobile && !collapsed,
        },
        // Extra large screens: max content width
        'xl:pr-6',
        className
      )}
    >
      <div className="w-full max-w-full overflow-hidden">{children}</div>
    </main>
  );
}
